import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useI18n } from "../i18n-context";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const { t, lang } = useI18n();
  const [data, setData] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const u = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...data, locale: lang });
      toast.success(t.contact.form.success);
      setData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const emails = [
    { k: "info", e: "info@akwaan.express", l: t.contact.emails.info },
    { k: "operation", e: "operation@akwaan.com", l: t.contact.emails.operation },
    { k: "sales", e: "sales@akwaan.com", l: t.contact.emails.sales },
  ];

  return (
    <>
      <PageHeader kicker={t.contact.kicker} title={t.contact.title} lead={t.contact.lead} />

      <section className="pb-24">
        <div className="container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-3">
            {emails.map((m) => (
              <a
                key={m.k}
                href={`mailto:${m.e}`}
                className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-brand-ink/10 hover:border-brand-blue hover:shadow-md transition-all group"
                data-testid={`contact-email-${m.k}`}
              >
                <div className="h-11 w-11 rounded-xl bg-brand-blue/10 flex items-center justify-center group-hover:bg-brand-blue group-hover:text-white transition-colors">
                  <Mail className="h-5 w-5 text-brand-blue group-hover:text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs uppercase tracking-wider font-mono text-brand-ink/50">{m.l}</div>
                  <div className="font-semibold text-brand-ink truncate">{m.e}</div>
                </div>
                <ArrowUpRight className="h-4 w-4 text-brand-ink/40 group-hover:text-brand-blue" />
              </a>
            ))}

            <div className="flex items-start gap-4 p-4 rounded-2xl bg-white border border-brand-ink/10">
              <div className="h-11 w-11 rounded-xl bg-brand-ink flex items-center justify-center flex-shrink-0">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="text-brand-ink/80 pt-2">{t.contact.address}</div>
            </div>

            {t.contact.phones.map((p) => (
              <a
                key={p}
                href={`tel:${p.replace(/\s/g, "")}`}
                dir="ltr"
                className="flex items-center gap-4 p-5 rounded-2xl bg-brand-blue text-white border border-brand-blue hover:bg-brand-blueDark transition-all shadow-md"
                data-testid={`contact-phone-${p}`}
              >
                <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Phone className="h-6 w-6 text-white" />
                </div>
                <div className="font-display font-extrabold text-lg md:text-xl">{p}</div>
              </a>
            ))}
          </div>

          <form
            onSubmit={submit}
            className="lg:col-span-7 bg-white rounded-3xl p-8 border-2 border-brand-ink/5 shadow-sm space-y-5 self-start"
            data-testid="contact-form"
          >
            <div>
              <h3 className="font-display font-bold text-2xl text-brand-ink mb-1">{t.contact.formTitle}</h3>
              <p className="text-sm text-brand-ink/55">{t.contact.lead}</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="field-label">{t.contact.form.name} *</label>
                <input type="text" required value={data.name} onChange={u("name")} className="field-input" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="field-label">{t.contact.form.email} *</label>
                <input type="email" required value={data.email} onChange={u("email")} className="field-input" data-testid="contact-email-input" />
              </div>
            </div>

            <div>
              <label className="field-label">{t.contact.form.phone}</label>
              <input type="tel" value={data.phone} onChange={u("phone")} className="field-input" data-testid="contact-phone-input" />
            </div>
            <div>
              <label className="field-label">{t.contact.form.subject} *</label>
              <input type="text" required value={data.subject} onChange={u("subject")} className="field-input" data-testid="contact-subject-input" />
            </div>
            <div>
              <label className="field-label">{t.contact.form.message} *</label>
              <textarea rows={5} required value={data.message} onChange={u("message")} className="field-input resize-none" data-testid="contact-message-input" />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full !py-4 disabled:opacity-60"
              data-testid="contact-submit-btn"
            >
              {loading ? t.contact.form.submitting : t.contact.form.submit}
              <ArrowRight className="h-4 w-4 flip-x" />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
